import React, { useEffect, useState } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus, MapPin, Phone, Globe, DollarSign, ShieldAlert } from "lucide-react";

// A small, curated IANA timezone list keeps the picker practical for a
// spa that mostly operates in South-East Asia + the Gulf. The <Select>
// still accepts a fully-typed zone string via the fallback custom input.
const TZ_PRESETS = [
  "Asia/Makassar",
  "Asia/Jakarta",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Bahrain",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
  "Australia/Sydney",
];

const CURRENCY_PRESETS = ["IDR", "BHD", "AED", "SGD", "THB", "USD", "EUR", "GBP", "AUD", "INR"];

const emptyForm = () => ({
  name: "",
  address: "",
  phone: "",
  currency: "IDR",
  timezone: "Asia/Makassar",
});

/**
 * Brand → Branch Management (Owner-only).
 *
 * Provisions new locations end-to-end: name/address/phone + local
 * currency + IANA timezone. On save the backend auto-seeds a 7-day
 * "Day Off" opening_hours template, default appointment_settings, a
 * "Treatment Room 1" placeholder so the calendar has capacity, and
 * ties the price_mode to `plus_tax` (matches the existing seed
 * branches). Services default to "available everywhere" — no manual
 * mapping needed until the operator wants to scope one.
 */
export default function BrandBranches() {
  const { staff } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const canManage = isOwner(staff);

  const load = async () => {
    setLoading(true);
    try {
      const r = await staffApi.get("/branches");
      setBranches(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const canSubmit = () =>
    !!form.name.trim() && !!form.address.trim() && !!form.phone.trim() && !!form.currency && !!form.timezone;

  const submit = async () => {
    if (!canSubmit()) {
      toast.error("Please fill Name, Address, Phone, Currency and Timezone.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        currency: (form.currency || "IDR").toUpperCase(),
        timezone: form.timezone,
      };
      await staffApi.post("/branches", payload);
      toast.success(`Branch "${payload.name}" created. Opening hours default to closed — configure them in Branch Settings.`);
      setDialogOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!canManage) {
    return (
      <StaffShell context="brand">
        <div className="max-w-2xl mx-auto p-6" data-testid="brand-branches-denied">
          <Card>
            <CardContent className="p-6 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-rose-700 mt-0.5" />
              <div>
                <h1 className="text-lg font-semibold">Branch Management is Owner-only</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Creating and provisioning new locations touches billing, currency, timezone and Start-of-Day gating. Only the Owner login can do this.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </StaffShell>
    );
  }

  return (
    <StaffShell context="brand">
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="brand-branches-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Branch Management</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Owner-only. Provision new Cocoon locations here. Each new branch is seeded with a 7-day "Day Off" opening schedule, a placeholder treatment room, and its own local currency + timezone — flip everything on from Branch Settings once the site is ready.
            </p>
          </div>
          <Button onClick={() => { setForm(emptyForm()); setDialogOpen(true); }} className="rounded-full" data-testid="brand-branches-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New branch
          </Button>
        </div>

        <Card data-testid="brand-branches-card">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold">All branches</h3>
              <span className="text-xs text-muted-foreground" data-testid="brand-branches-count">({branches.length})</span>
            </div>
            {loading ? (
              <p className="py-6 text-sm text-muted-foreground text-center">Loading…</p>
            ) : branches.length === 0 ? (
              <p className="py-8 text-sm text-muted-foreground text-center">No branches yet — click "New branch" to create the first one.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Timezone</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {branches.map((b) => (
                    <TableRow key={b.id} data-testid={`brand-branches-row-${b.id}`}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[220px]" title={b.address || ""}>{b.address || "—"}</TableCell>
                      <TableCell className="text-xs">{b.phone || "—"}</TableCell>
                      <TableCell className="text-xs font-mono">{b.currency || "IDR"}</TableCell>
                      <TableCell className="text-xs font-mono">{b.timezone || "Asia/Makassar"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="brand-branches-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Building2 className="w-4 h-4" /> New branch</DialogTitle>
              <DialogDescription>
                We'll auto-seed rooms + a closed 7-day opening schedule so nothing books until you configure it in Branch Settings.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div>
                <Label className="text-xs">Branch name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus placeholder="e.g. Cocoon Bahrain" data-testid="brand-branches-input-name" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Building, street, city" data-testid="brand-branches-input-address" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+973 …" data-testid="brand-branches-input-phone" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs flex items-center gap-1"><DollarSign className="w-3 h-3" /> Local currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="brand-branches-input-currency" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCY_PRESETS.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`brand-branches-currency-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Timezone</Label>
                  <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                    <SelectTrigger data-testid="brand-branches-input-timezone" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TZ_PRESETS.map((tz) => (
                        <SelectItem key={tz} value={tz} data-testid={`brand-branches-tz-${tz}`}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Currency drives every POS ticket at this location. Timezone drives Start-of-Day gating and "today" on the calendar.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={busy} data-testid="brand-branches-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy || !canSubmit()} data-testid="brand-branches-save">
                {busy ? "Creating…" : "Create branch"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </StaffShell>
  );
}
